import * as types from './mutation_types';
import fetch from "../../../utils/fetch";
import router from '@/router';
import {setFlag, syncFlagsFromBackend} from '@/config/featureFlags';
import i18n from '@/i18n';

export const reset = ({ commit }) => {
    commit(types.USER_RESET);
};
export const resetError = ({commit}) => {
    commit(types.USER_SET_ERROR, '');
};
export const setError = ({commit}, message) => {
    commit(types.USER_SET_ERROR, message);
};
export const logout = ({commit}) => {
    commit(types.USER_LOGOUT);
};

export const loadItems = ({commit, dispatch}) => {
    commit(types.USER_SET_LOADING, true);
    return fetch(`accounts?itemsPerPage=300`, {dispatch: dispatch})
        .then((response) => response.json())
        .then((data) => {
            commit(types.USER_SET_LOADING, false);
            commit(types.USER_SET_USERS, data['member']);
        })
        .catch((e) => {
            commit(types.USER_SET_LOADING, false);
            commit(types.USER_SET_ERROR, e.message);
        });
};

// Admin prehled posledni aktivity hracu (jen Kelghor, viz isDebugUser v
// NavDrawer.vue), viz docs/superpowers/specs/2026-08-18-player-activity-online-status-design.md.
export const loadPlayersActivity = ({dispatch}, {page = 1, limit = 10, search = ''} = {}) => {
    const params = new URLSearchParams({page, limit});
    if (search) {
        params.set('search', search);
    }
    return fetch(`players-activity?${params.toString()}`, {dispatch: dispatch})
        .then((response) => response.json())
        .catch(() => ({items: [], total: 0, page, limit}));
};

export const loadItem = ({commit, dispatch}, { id, explicitToken = null }) => {
    commit(types.USER_SET_LOADING, true);
    const fetchOptions = { dispatch: dispatch };
    if (explicitToken) {
        fetchOptions.explicitToken = explicitToken;
    }
    return fetch(`accounts/${id}`, fetchOptions)
        .then((response) => response.json())
        .then((data) => {
            let user = {
                id: data.trainer.id,
                name: data.name || data.email || null,
                musicVolume: data.musicVolume || 0.5,
                buttonVolume: data.buttonVolume || 0.5,
                notificationSound: data.notificationSound || 'default',
                // "|| true" by tise prepsalo explicitni false na true - typeof check misto toho.
                notificationsEnabled: typeof data.notificationsEnabled === 'boolean' ? data.notificationsEnabled : true,
                position: data.position ? {
                    id: data.position.id,
                    steps: data.position.steps,
                } : null,
            };

            commit(types.USER_SET_USER, user);
            commit(types.USER_SET_LOADING, false);
            return user;
        })
        .catch((e) => {
            commit(types.USER_SET_LOADING, false);
            commit(types.USER_SET_ERROR, e.message || i18n.t('common.apiErrors.loadUserFailed'));
            throw e;
        });
};

// Sdílené se stejnou logikou pro login i impersonate - impersonate je v zásadě
// jen "přihlášení jako někdo jiný", takže musí uložit uživatele úplně stejně.
const applyLoginResponse = (commit, accData) => {
    let data = accData.trainer;
    let user = {
        id: data.id,
        name: data.name,
        musicVolume: data.musicVolume,
        buttonVolume: data.buttonVolume,
        notificationSound: data.notificationSound || 'default',
        notificationsEnabled: typeof data.notificationsEnabled === 'boolean' ? data.notificationsEnabled : true,
        position: {
            id: data.position.id,
            steps: data.position.steps,
        }
    };

    commit(types.USER_SET_USER, user);
    commit(types.USER_SET_TOKEN, accData.token);
    commit(types.USER_SET_USERID, user.id.toString());

    // newUi je vzdy zapnute po prihlaseni pro vsechny uzivatele, bez ohledu na
    // hostname/localStorage stav prohlizece - jde rucne vypnout zpet (viz
    // debug switch v App.vue).
    setFlag('newUi', true);

    // Fire-and-forget, pokryva oba zpusoby prihlaseni (login i impersonate) -
    // pretaha server hodnoty Options nastaveni hned po ziskani noveho tokenu.
    syncFlagsFromBackend();

    return user;
};

export const login = ({commit, dispatch},obj) => {
    commit(types.USER_SET_LOADING, true);
    const path = 'login-user';
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const options = {
        method: 'POST',
        body: JSON.stringify(obj),
        headers: myHeaders,
        dispatch: dispatch
    };

    return fetch(path, options)
        .then((response) => {
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return null;
            }
            return response.json();
        })
        .then(
            (accData) => {
                if (!accData || !accData.token) {
                    console.error('LOGIN ACTION: Backend nevrátil platná data nebo token.');
                    throw new Error(i18n.t('common.apiErrors.invalidServerResponse'));
                }
                applyLoginResponse(commit, accData);
                commit(types.USER_SET_LOADING, false);

                try {
                    if (router.currentRoute.path !== '/journey') {
                        router.push({ name: 'Journey' });
                    }
                } catch (routerError) {
                    console.error('LOGIN ACTION: Chyba při volání router.push!', routerError);
                }
            })
        .catch((e) => {
            console.error('LOGIN ACTION: Chyba v fetch nebo .then() bloku!', e);
            commit(types.USER_SET_LOADING, false);
            const errorMsg = e.errors ? e.errors._error : e.message;
            commit(types.USER_SET_ERROR, errorMsg || i18n.t('common.apiErrors.networkOrInvalidData'));
            throw e;
        });
};

export const impersonate = ({state, commit, dispatch}, id) => {
    commit(types.USER_SET_LOADING, true);

    if (!state.adminToken) {
        commit(types.USER_STASH_ADMIN, {
            token: state.token,
            user: state.item,
            userId: state.userId,
        });
    }

    return fetch(`accounts/${id}/impersonate`, {method: 'POST', dispatch: dispatch})
        .then((response) => response.json())
        .then((accData) => {
            applyLoginResponse(commit, accData);
            commit(types.USER_SET_LOADING, false);

            // Plný reload, ne router.push - všechny ostatní store moduly (journey,
            // trainer, pokemon, dashboard, city...) si drží stav předchozího uživatele
            // a nic ho po přepnutí sám neinvaliduje. Token/adminStash jsou v
            // sessionStorage, takže reload o přepnutí nepřijde.
            window.location.href = '/journey';
        })
        .catch((e) => {
            commit(types.USER_SET_LOADING, false);
            commit(types.USER_SET_ERROR, e.message || i18n.t('common.apiErrors.switchUserFailed'));
            throw e;
        });
};

export const returnToAdmin = ({state, commit}) => {
    if (!state.adminToken) {
        return Promise.resolve();
    }

    commit(types.USER_SET_USER, state.adminUser);
    commit(types.USER_SET_TOKEN, state.adminToken);
    commit(types.USER_SET_USERID, state.adminUserId);
    commit(types.USER_CLEAR_ADMIN_STASH);

    // Plný reload ze stejného důvodu jako v impersonate() - viz komentář tam.
    window.location.href = '/journey';

    return Promise.resolve();
};

export const editUser = ({commit}, data) => {
    let user = {
        id : data.id,
        name : data.name,
        musicVolume: data.musicVolume,
        buttonVolume: data.buttonVolume,
        notificationSound: data.notificationSound || 'default',
        notificationsEnabled: typeof data.notificationsEnabled === 'boolean' ? data.notificationsEnabled : true,
        position : {
            id : data.position.id,
            steps : data.position.steps,
        }
    };
    commit(types.USER_SET_USER, user);
};

export const fetchObject = ({commit, dispatch},obj) => {
    commit(types.USER_SET_LOADING, true);
    const path = obj.url;
    const options = { dispatch: dispatch };

    if (obj.method === 'GET'){
        return fetch(path, options)
            .then((response) => response.json())
            .then((data) => {
                commit(types.USER_SET_LOADING, false);
                return data;
            })
            .catch((e) => {
                commit(types.USER_SET_LOADING, false);
                commit(types.USER_SET_ERROR, e.message);
                throw e;
            });
    }

    options.method = obj.method;
    options.body = JSON.stringify(obj.obj);

    const myHeaders = new Headers();
    // Server vyzaduje 'application/merge-patch+json' pro PATCH, ne obycejny JSON.
    if (obj.method === 'PATCH') {
        myHeaders.append('Content-Type', 'application/merge-patch+json');
    } else {
        myHeaders.append('Content-Type', 'application/json');
    }
    options.headers = myHeaders;

    return fetch(path, options)
        .then((response) => {
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return null;
            }
            return response.json();
        })
        .then((data) => {
            commit(types.USER_SET_LOADING, false);
            return data;
        })
        .catch((e) => {
            commit(types.USER_SET_LOADING, false);
            commit(types.USER_SET_ERROR, e.message);
            throw e;
        });
};